import React, { type FC } from 'react'
import { Box, DialogContent, Link, type SxProps, type Theme, Typography } from '@mui/material'
import { CloudUpload } from '@mui/icons-material'

import { type ConnectwareError, isManagedServiceUpToDate, Translation } from '../../../../domain'

import { DocumentationType } from '../../../../application'

import { readFirstFileAsText } from '../..'
import { useAsyncCallback } from '../../Callback'
import { Button, FileUpload, MediumParagraph, Table } from '../../common'
import { DocumentationLink } from '../../Documentation'
import { createFormatter, FormattedDateTime, FormattedList, FormattedTranslation, useTranslator } from '../../Internationalization'
import { AbsoluteRouteOnlyPath, useRouting } from '../../routing'
import {
    useCancel,
    useFile,
    useHasNumberedSteps,
    useIsEditable,
    useManaged,
    useOnFileChange,
    useShouldShowUploadInstructions,
    useSupportedCommissioningFileTypes,
} from './Hooks'

const uploadFormStyle: SxProps<Theme> = { textAlign: 'center' }
const uploadDialogContentStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column' }
const uploadDialogEntryStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }
const uploadIconStyle: SxProps<Theme> = { mr: 4, color: 'secondary.main' }
const tableWrapperStyle: SxProps<Theme> = { my: 4 }

const SimpleFileUploadSection: FC = () => {
    const file = useFile()
    const types = useSupportedCommissioningFileTypes()
    const onFileChange = useOnFileChange()
    const isEditable = useIsEditable()

    const [readFile] = useAsyncCallback(
        async (files: readonly File[]) => {
            const content = await readFirstFileAsText(files).catch((e: ConnectwareError) => e)
            onFileChange(content)
        },
        [onFileChange]
    )

    const managed = useManaged()

    if (managed !== null) {
        return null
    }

    return (
        <Box data-testid="file-upload-section" sx={uploadDialogEntryStyle}>
            <CloudUpload sx={uploadIconStyle} />
            <FileUpload readOnly={!isEditable} initialValue={file ? [file] : undefined} accept={types} onChange={readFile} />
        </Box>
    )
}

const ManagedFileUploadSection: FC = () => {
    const onFileChange = useOnFileChange()
    const translator = useTranslator()

    const managed = useManaged()

    if (managed === null || isManagedServiceUpToDate(managed)) {
        return null
    }

    const { commissioningFile: newCommissioningFile, updatedAt: newUpdatedAt, version: newVersion } = managed.new
    const { version: currentVersion, updatedAt: currentUpdatedAt } = managed.current

    return (
        <>
            <Box sx={tableWrapperStyle}>
                <Table
                    data={[
                        { label: translator.formatTranslation(Translation.VERSION), current: currentVersion, new: newVersion },
                        {
                            label: translator.formatTranslation(Translation.LAST_UPDATED),
                            current: <FormattedDateTime date={currentUpdatedAt} format="datetime" />,
                            new: <FormattedDateTime date={newUpdatedAt} format="datetime" />,
                        },
                    ]}
                    columns={{ label: {}, current: { label: Translation.CURRENT }, new: { label: Translation.NEW } }}
                />
            </Box>
            <Button data-testid="update-service-from-catalog" variant="secondary" onClick={() => onFileChange(newCommissioningFile)}>
                <FormattedTranslation id={Translation.USE_VERSION} values={{ version: newVersion }} />
            </Button>
        </>
    )
}

const InstructionsSection: FC = () => {
    const supportedCommissioningFileTypes = useSupportedCommissioningFileTypes()
    const redirect = useRouting()

    const hasSteps = useHasNumberedSteps()
    const cancel = useCancel()
    const managed = useManaged()
    const shouldShowUploadInstructions = useShouldShowUploadInstructions()

    if (!shouldShowUploadInstructions) {
        return <MediumParagraph id={Translation.COMMISSIONING_FILE} align="left" />
    }

    return (
        <>
            <Typography data-testid="file-upload-summary-1" variant="body1" align="left">
                {hasSteps && '1. '}
                <FormattedTranslation
                    id={managed === null ? Translation.SERVICE_SELECT_COMMISSIONING_FILE : Translation.SERVICE_UPDATE_FROM_CATALOG}
                    values={{
                        types: <FormattedList type="disjunction" value={supportedCommissioningFileTypes} />,
                        servicesCatalogLink: redirect.isAtRoute(AbsoluteRouteOnlyPath.SERVICES_CATALOG)
                            ? createFormatter(({ children }) => <>{children}</>, {})
                            : createFormatter(Link, {
                                  onClick: () => {
                                      cancel()
                                      redirect.redirect(AbsoluteRouteOnlyPath.SERVICES_CATALOG)
                                  },
                              }),
                    }}
                />
            </Typography>
            <Typography data-testid="file-upload-summary-2" variant="body2" align="left">
                <FormattedTranslation
                    id={
                        managed === null
                            ? Translation.SERVICE_DOCUMENTATION_LINK
                            : isManagedServiceUpToDate(managed)
                            ? Translation.SERVICE_UPDATE_NOT_AVAILABLE
                            : Translation.SERVICE_UPDATE_AVAILABLE
                    }
                    values={{
                        documentationLink: createFormatter(DocumentationLink, { type: DocumentationType.SERVICES }),
                        managedName: (
                            <b>
                                {managed?.directory}
                                {managed?.filename}
                            </b>
                        ),
                    }}
                />
            </Typography>
        </>
    )
}

export const FileUploadSection: FC = () => (
    <Box sx={uploadFormStyle}>
        <DialogContent sx={uploadDialogContentStyle}>
            <InstructionsSection />
            <SimpleFileUploadSection />
            <ManagedFileUploadSection />
        </DialogContent>
    </Box>
)
